import {Flex, Text} from '@chakra-ui/react';
import {animate, motion, useMotionValue, useTransform} from 'framer-motion';
import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {TimerIcon} from '../icons/TimerIcon';
import dayjs from 'dayjs';

import {useTriviaStore} from '../state';

type QuestionTimerProps = {
  onTimeOut: () => void;
  isPaused?: boolean;
};

export function QuestionTimer({onTimeOut, isPaused = false}: QuestionTimerProps) {
  const duration = useTriviaStore((state) => state.challenge?.duration_per_question!);

  const [countdown, setCountdown] = useState(duration);
  const [startTimestamp] = useState(dayjs());
  const progress = useMotionValue(0);

  useEffect(() => {
    if (isPaused && countdown > 0) return progress.stop();

    animate(progress, 1 - (countdown - 1) / duration, {duration: 1, ease: 'linear'});
  }, [countdown, isPaused, progress, duration]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPaused || countdown === 0) return;
      const newCount = Math.max(duration - dayjs().diff(startTimestamp, 'second'), 0);
      if (!newCount) onTimeOut();
      setCountdown(newCount);
    }, 1000);
    return () => clearInterval(interval);
  }, [countdown, isPaused, onTimeOut, startTimestamp, duration]);

  const format = useIntl().formatNumber;
  const backgroundColor = useTransform(progress, [0, 0.5, 1], ['#5AC57B', '#F49300', '#CB2525']);
  const width = useTransform(progress, [0, 1], ['100%', '0%']);

  return (
    <Flex
      w={['100%', null, '70%', '40%']}
      h="32px"
      bg="#EFEFEF"
      border="0.5px solid #C7C7C7"
      rounded="74px"
      px="2"
      py="1.5"
      align="flex-end"
      justify="center"
      flexDir="column"
      position="relative"
    >
      <motion.div
        style={{
          backgroundColor,
          width,
          height: '20px',
          borderRadius: '60px',
        }}
      />

      <Flex position="absolute" textAlign="right" p="1" gap="1" align="center" justify="center">
        <Text fontWeight="700" fontSize="16px" lineHeight="20px">
          {format(countdown)}
        </Text>

        <TimerIcon />
      </Flex>
    </Flex>
  );
}
